'use client';
import { useEffect } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import SectionHeader from '@/components/ui/SectionHeader';
import AppImage from '@/components/ui/AppImage';

export default function TrademarksPage() {
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('active')),
      { threshold: 0.1 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background text-foreground pt-24">
        <section className="py-24 px-6 border-b border-border">
          <div className="max-w-[1600px] mx-auto">
            <SectionHeader
              eyebrow="Legal"
              title="Trademarks"
              description="Last updated: February 18, 2026"
              align="center"
              className="mb-16"
            />
          </div>
        </section>

        <section className="py-24 px-6">
          <div className="max-w-[900px] mx-auto">
            <div className="space-y-12">
              {/* What is a Trademark */}
              <div className="p-8 border border-border hover:border-primary/50 transition-colors bg-card">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Definition</span>
                <h3 className="text-2xl font-bold mb-6">What is a Trademark?</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  A trademark is a symbol, design, phrase, word, logo, any other device, or a combination of the preceding, which is used by a company in order to distinguish its products from those of their competitors and other companies within the same industry.
                </p>
              </div>

              {/* What is a Service Mark */}
              <div className="p-8 border border-border hover:border-primary/50 transition-colors bg-card">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Definition</span>
                <h3 className="text-2xl font-bold mb-6">What is a Service Mark?</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed">
                  A service mark serves the same purpose as a trademark, but is different in that it identifies a service offered by a company rather than a physical product. Service marks are used for intangible goods, while trademarks are used for tangible goods. Legally, the same guidelines and legal principles are applied to service marks as they are to trademarks. Oftentimes, the terms "trademark" and "mark" are used in a manner which applies to both a company's services and products.
                </p>
              </div>

              {/* How to Use a Mark */}
              <div className="p-8 border border-border hover:border-primary/50 transition-colors bg-card">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Guidelines</span>
                <h3 className="text-2xl font-bold mb-6">How to Use a Mark</h3>
                <div className="space-y-6">
                  <p className="text-body-sm text-muted-foreground leading-relaxed">
                    When using a trademark or service mark only in the United States, append the TM, SM or ® symbol after first use of each mark. If use of the mark reaches audiences outside of the United States, do not append the symbols. Instead, use an appropriate trademark attribution notice such as "Mac Poker is a trademark of Best Odds Corp., and has been registered in the United States and other countries."
                  </p>
                  <p className="text-body-sm text-muted-foreground leading-relaxed">
                    If you wish to describe use of an Apple Macintosh computer for use with poker play, then you should use language comparable to that, i.e., "for poker play with a Mac…".
                  </p>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">
                    When using a Best Odds Corp. trademark, apply the following rules:
                  </p>
                  <ul className="space-y-3 ml-6">
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Use a trademark as an adjective, followed by an appropriate noun descriptor.</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Do not use a trademark in possessive form, i.e. "Mac Poker's" is inappropriate use of a trademark.</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Do not use a trademark in plural form.</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Never hyphenate a trademark, i.e. "Mac-poker" is an inappropriate use of a trademark.</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Never alter a trademark. Substantially similar use of a trademark can be deemed as "confusingly similar."</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Never portray a trademark in a negative description as it will lower the value of the mark.</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Use proper capitalization of a trademark, or make the mark conspicuous each time it is used (e.g., bold type, ALL CAPITAL LETTERS, or "Quotations.").</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Use lowercase letters for the generic noun(s) following a trademark.</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Attribute the trademark symbol accurately (TM, SM or ®), and at least once per page.</span>
                    </li>
                    <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                      <span className="text-primary mr-3 mt-1">•</span>
                      <span>Never register a domain name with a trademark in it.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Best Odds Corp's Service Marks */}
              <div className="p-8 border border-border hover:border-primary/50 transition-colors bg-card">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Our Marks</span>
                <h3 className="text-2xl font-bold mb-6">Best Odds Corp.'s Service Marks</h3>
                <div className="space-y-4">
                  <p className="text-body-sm text-muted-foreground leading-relaxed">
                    The absence of a product, service name, or logo from the following list of marks does not constitute a waiver of Best Odds Corp.'s trademark or other intellectual property rights concerning that item.
                  </p>
                  <div className="bg-surface-2 border border-border p-6 rounded">
                    <p className="text-body-sm font-semibold text-foreground mb-4">
                      BEST ODDS CORP'S SERVICE MARK — GENERIC NOUN TO FOLLOW(S)
                    </p>
                    <p className="text-body-sm text-muted-foreground mb-4">
                      Mac Poker — poker site
                    </p>
                    <p className="text-body-sm text-muted-foreground italic">
                      Note: These generic terms are only suggestions, and there may be other words that are equally appropriate.
                    </p>
                  </div>
                </div>
              </div>

              {/* Best Odds Corp Logos */}
              <div className="p-8 border border-border hover:border-primary/50 transition-colors bg-card">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Brand Assets</span>
                <h3 className="text-2xl font-bold mb-6">Best Odds Corp Logos</h3>
                <div className="bg-surface-2 border border-border p-12 rounded flex justify-center items-center">
                  <AppImage
                    src="/assets/images/image-1771404415531.png"
                    alt="Best Odds Corp Logo"
                    width={240}
                    height={240}
                    className="max-w-full h-auto"
                  />
                </div>
              </div>

              {/* Third-Party Trademarks */}
              <div className="p-8 border border-border hover:border-primary/50 transition-colors bg-card">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Third-Party</span>
                <h3 className="text-2xl font-bold mb-6">Third-Party Trademarks</h3>
                <p className="text-body-sm text-muted-foreground leading-relaxed mb-6">
                  The following is a list of third-party trademarks and service marks that are used within the Best Odds Corp. network of internet websites.
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>WSOP, World Series of Poker, and the WSOP Chip Logo are trademarks of Harrah's Interactive Entertainment, Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Legends of Poker is a trademark of The Bicycle Casino, Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>BlackBerry, BlackBerry Storm, SurePress, and the BlackBerry Logo are registered trademarks of Research In Motion Limited. Best Odds Corp. is not endorsed, sponsored, affiliated with or otherwise authorized by Research In Motion Limited.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Sports Illustrated is a registered trademark of Time, Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>WWE (world wrestling).</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>WPT is a trademark of WPT Enterprises, Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>HoldemPoker.com is a registered trademark of Ongame e-solutions AB LTD LIAB CO.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>GoDaddy.com and Go Daddy are registered trademarks of GoDaddy.com, Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Heads Up Poker Championship is a trademark of NBC Universal, Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Windows is a registered trademark of Microsoft Corporation in the United States and other countries.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Super Bowl, NFL, the Super Bowl Logo, and the NFL Shield Design are registered trademarks of the National Football League.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Hard Rock Casino is a registered trademark of Hard Rock Cafe International (USA), Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Texas Calculate and Calculate Pro are registered trademarks of Texas Calculate, LLC.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>iMEGA is a registered trademark of OAC Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Partypoker.com is a registered trademark of WPC Productions, Inc.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Bodog is a registered trademark of Bodog Entertainment Group S.A. CORPORATION COSTA RICA.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>PokerStars is a registered trademark of RATIONAL ENTERTAINMENT ENTERPRISES LIMITED.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>iOS is a trademark of Cisco.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>OnGame and PokerRoom.com are registered trademarks of Ongame e-solutions AB.</span>
                  </li>
                  <li className="text-body-sm text-muted-foreground leading-relaxed flex items-start">
                    <span className="text-primary mr-3 mt-1">•</span>
                    <span>Absolute Poker is a registered trademark of Absolute Entertainment SA LTD.</span>
                  </li>
                </ul>
              </div>

              {/* Apple Trademarks */}
              <div className="p-8 border border-border hover:border-primary/50 transition-colors bg-card">
                <span className="text-micro uppercase tracking-ultra-wide text-primary block mb-4">Apple Inc.</span>
                <h4 className="text-xl font-bold mb-6">The following are trademarks of Apple Inc., registered in the U.S. and other countries:</h4>
                <div className="bg-surface-2 border border-border rounded overflow-hidden">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
                    {/* Column 1 */}
                    <div className="bg-surface-2 p-6 space-y-3">
                      <p className="text-body-sm text-muted-foreground">AirPort</p>
                      <p className="text-body-sm text-muted-foreground">AirPort Express</p>
                      <p className="text-body-sm text-muted-foreground">AirPort Extreme</p>
                      <p className="text-body-sm text-muted-foreground">AirTunes</p>
                      <p className="text-body-sm text-muted-foreground">Apple</p>
                      <p className="text-body-sm text-muted-foreground">Apple logo</p>
                      <p className="text-body-sm text-muted-foreground">Apple Cinema Display</p>
                      <p className="text-body-sm text-muted-foreground">Apple Remote Desktop</p>
                      <p className="text-body-sm text-muted-foreground">AppleTalk</p>
                      <p className="text-body-sm text-muted-foreground">Apple TV</p>
                      <p className="text-body-sm text-muted-foreground">AppleVision</p>
                      <p className="text-body-sm text-muted-foreground">Back to My Mac</p>
                      <p className="text-body-sm text-muted-foreground">Bonjour</p>
                      <p className="text-body-sm text-muted-foreground">Bonjour logo</p>
                      <p className="text-body-sm text-muted-foreground">Boot Camp</p>
                      <p className="text-body-sm text-muted-foreground">ColorSync</p>
                      <p className="text-body-sm text-muted-foreground">ColorSync logo</p>
                      <p className="text-body-sm text-muted-foreground">Cover Flow</p>
                      <p className="text-body-sm text-muted-foreground">Exposé</p>
                      <p className="text-body-sm text-muted-foreground">FaceTime</p>
                      <p className="text-body-sm text-muted-foreground">FileVault</p>
                      <p className="text-body-sm text-muted-foreground">Finder</p>
                      <p className="text-body-sm text-muted-foreground">FireWire</p>
                      <p className="text-body-sm text-muted-foreground">Gadget</p>
                      <p className="text-body-sm text-muted-foreground">GarageBand</p>
                      <p className="text-body-sm text-muted-foreground">iBook</p>
                    </div>
                    {/* Column 2 */}
                    <div className="bg-surface-2 p-6 space-y-3">
                      <p className="text-body-sm text-muted-foreground">iBooks</p>
                      <p className="text-body-sm text-muted-foreground">iCal</p>
                      <p className="text-body-sm text-muted-foreground">iChat</p>
                      <p className="text-body-sm text-muted-foreground">iDVD</p>
                      <p className="text-body-sm text-muted-foreground">iLife</p>
                      <p className="text-body-sm text-muted-foreground">iMac</p>
                      <p className="text-body-sm text-muted-foreground">iMovie</p>
                      <p className="text-body-sm text-muted-foreground">iPad</p>
                      <p className="text-body-sm text-muted-foreground">iPhone</p>
                      <p className="text-body-sm text-muted-foreground">iPhoto</p>
                      <p className="text-body-sm text-muted-foreground">iPod</p>
                      <p className="text-body-sm text-muted-foreground">iPod classic</p>
                      <p className="text-body-sm text-muted-foreground">iPod Hi-Fi</p>
                      <p className="text-body-sm text-muted-foreground">iPod nano</p>
                      <p className="text-body-sm text-muted-foreground">iPod shuffle</p>
                      <p className="text-body-sm text-muted-foreground">iPod touch</p>
                      <p className="text-body-sm text-muted-foreground">iSight</p>
                      <p className="text-body-sm text-muted-foreground">iTunes</p>
                      <p className="text-body-sm text-muted-foreground">iTunes Logo</p>
                      <p className="text-body-sm text-muted-foreground">iWeb</p>
                      <p className="text-body-sm text-muted-foreground">iWork</p>
                      <p className="text-body-sm text-muted-foreground">Keychain</p>
                      <p className="text-body-sm text-muted-foreground">Leopard</p>
                      <p className="text-body-sm text-muted-foreground">Mac</p>
                      <p className="text-body-sm text-muted-foreground">Mac logo</p>
                      <p className="text-body-sm text-muted-foreground">MacBook</p>
                    </div>
                    {/* Column 3 */}
                    <div className="bg-surface-2 p-6 space-y-3">
                      <p className="text-body-sm text-muted-foreground">MacBook Air</p>
                      <p className="text-body-sm text-muted-foreground">Macintosh</p>
                      <p className="text-body-sm text-muted-foreground">Mac OS</p>
                      <p className="text-body-sm text-muted-foreground">Made for iPad logo</p>
                      <p className="text-body-sm text-muted-foreground">Made for iPhone logo</p>
                      <p className="text-body-sm text-muted-foreground">Made for iPod logo</p>
                      <p className="text-body-sm text-muted-foreground">MagSafe</p>
                      <p className="text-body-sm text-muted-foreground">Multi-Touch</p>
                      <p className="text-body-sm text-muted-foreground">Podcast Logo</p>
                      <p className="text-body-sm text-muted-foreground">QuickTime</p>
                      <p className="text-body-sm text-muted-foreground">QuickTime Broadcaster</p>
                      <p className="text-body-sm text-muted-foreground">QuickTime logo</p>
                      <p className="text-body-sm text-muted-foreground">QuickTime TV</p>
                      <p className="text-body-sm text-muted-foreground">Retina</p>
                      <p className="text-body-sm text-muted-foreground">Safari</p>
                      <p className="text-body-sm text-muted-foreground">Shake</p>
                      <p className="text-body-sm text-muted-foreground">Shuffle</p>
                      <p className="text-body-sm text-muted-foreground">SnapBack</p>
                      <p className="text-body-sm text-muted-foreground">Snow Leopard</p>
                      <p className="text-body-sm text-muted-foreground">Spaces</p>
                      <p className="text-body-sm text-muted-foreground">Spotlight</p>
                      <p className="text-body-sm text-muted-foreground">SuperDrive</p>
                      <p className="text-body-sm text-muted-foreground">Think different</p>
                      <p className="text-body-sm text-muted-foreground">Time Capsule</p>
                      <p className="text-body-sm text-muted-foreground">Time Machine</p>
                      <p className="text-body-sm text-muted-foreground">Works with iPhone logo</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}